import { useState, useEffect } from "react";
import { trpc } from "../lib/trpc";
import { Image as ImageIcon, Loader2 } from "lucide-react";
import { useTranslation } from "../i18n";

interface ImagePreviewProps {
  imageId: number;
  type?: "original" | "preview" | "thumbnail";
  alt?: string;
  className?: string;
}

export function ImagePreview({ imageId, type = "preview", alt = "Image", className = "" }: ImagePreviewProps) {
  const { t } = useTranslation();
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [error, setError] = useState(false);

  const { data, isLoading, error: queryError } = trpc.images.getSignedUrl.useQuery(
    { imageId, type },
    {
      // Refetch every 50 minutes (signed URLs expire after 1 hour)
      refetchInterval: 50 * 60 * 1000,
      retry: 1,
    }
  );

  useEffect(() => {
    if (data?.url) {
      setImageUrl(data.url);
      setError(false);
    } else if (queryError) {
      setError(true);
    }
  }, [data, queryError]);

  if (isLoading) {
    return (
      <div className={`flex items-center justify-center bg-muted ${className}`}>
        <Loader2 className="h-8 w-8 text-muted-foreground animate-spin" />
      </div>
    );
  }

  if (error || !imageUrl) {
    return (
      <div className={`flex flex-col items-center justify-center bg-muted ${className}`}>
        <ImageIcon className="h-16 w-16 text-muted-foreground mb-2" />
        <p className="text-xs text-muted-foreground text-center px-4">
          {queryError?.message?.includes("not configured") 
            ? t.images.gcsNotConfigured 
            : t.images.imageNotAvailable}
        </p>
      </div>
    );
  }

  return (
    <img
      src={imageUrl}
      alt={alt}
      className={`object-cover ${className}`}
      loading="lazy"
      onError={() => setError(true)}
    />
  );
}
